import { ThemeEvents, CartUpdateEvent } from '@theme/events';

class CrossSellPopup {
  constructor() {
    this.dialogComponent = document.getElementById('cross-sell-popup');
    if (!this.dialogComponent) return;

    this.list = this.dialogComponent.querySelector('[data-cross-sell-list]');
    this.addLabel = this.dialogComponent.getAttribute('data-add-label') || 'Add';
    this.addedLabel = this.dialogComponent.getAttribute('data-added-label') || 'Added';
    this.loadingLabel = this.dialogComponent.getAttribute('data-loading-label') || 'Adding...';
    this.soldOutLabel = this.dialogComponent.getAttribute('data-sold-out-label') || 'Sold out';
    this.currencyCode = this.dialogComponent.getAttribute('data-currency-code') || '';
    this.currencySymbol = this.dialogComponent.getAttribute('data-currency-symbol') || '';
    const maxItemsAttr = this.dialogComponent.getAttribute('data-max-items');
    this.maxItems = maxItemsAttr === null ? 4 : Number(maxItemsAttr);
    this.locale = document.documentElement.lang || navigator.language || 'en';

    this.crossSellMap = new Map();
    this.productCache = new Map();
    this.suppressNext = false;
    this.renderRequestId = 0;

    this.updateMapFromDom();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('cross-sell:product-added', this.onProductAdded);
    this.dialogComponent.addEventListener('click', this.onDialogClick);
  }

  updateMapFromDom() {
    const nodes = document.querySelectorAll('[data-product-id][data-cross-sell-handles]');
    nodes.forEach((node) => {
      const productId = node.getAttribute('data-product-id');
      const handles = this.parseHandles(node.getAttribute('data-cross-sell-handles'));
      if (!productId || handles.length === 0) return;
      this.crossSellMap.set(String(productId), handles);
    });
  }

  parseHandles(value) {
    if (!value) return [];
    return value
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  onProductAdded = async (event) => {
    return;

    const productId = event?.detail?.productId;
    if (!productId) return;

    const handles = this.parseHandles(event?.detail?.handles);
    const source = event?.detail?.source || '';
    const context = event?.detail?.context || '';

    await this.openForProductId(productId, handles, { source, context });
  };

  onCartUpdate = async (event) => {
    return;

    if (this.suppressNext) {
      this.suppressNext = false;
      return;
    }

    const data = event?.detail?.data || {};
    if (data.didError) return;

    const source = data.source;
    if (source !== 'collection-infinite-scroll') return;

    const productId = data.productId;
    if (!productId) return;

    await this.openForProductId(productId);
  };

  async openForProductId(productId, preferredHandles = [], options = {}) {
    await this.closeOpenCartDrawer();
    this.showLoadingState();

    if (options.source === 'product-form-component' && options.context === 'product-page') {
      this.openForActiveProducts(productId);
      return;
    }

    if (preferredHandles.length) {
      this.openForHandles(preferredHandles);
      return;
    }

    this.updateMapFromDom();
    const handles = this.crossSellMap.get(String(productId));
    if (!handles || handles.length === 0) {
      this.openForFallbackHandles(productId);
      return;
    }

    const limitedHandles = this.maxItems > 0 ? handles.slice(0, this.maxItems) : handles;
    this.openForHandles(limitedHandles);
  }

  async closeOpenCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    const openCartDialog = cartDrawer?.querySelector('dialog[open]');

    if (!cartDrawer || !openCartDialog) return;

    if (typeof cartDrawer.closeDialog === 'function') {
      await cartDrawer.closeDialog();
      return;
    }

    openCartDialog.close();
  }

  async openForActiveProducts(productId) {
    const handles = await this.fetchActiveProductHandles(productId);

    if (!handles.length) {
      this.showEmptyState();
      return;
    }

    this.openForHandles(handles);
  }

  async openForFallbackHandles(productId) {
    const handles = await this.fetchFallbackHandles(productId);
    if (!handles.length) return;
    this.openForHandles(handles);
  }

  async fetchFallbackHandles(productId) {
    const fallbackLimit = this.maxItems > 0 ? this.maxItems + 1 : 250;

    try {
      const response = await fetch(`/products.json?limit=${Math.max(5, fallbackLimit)}`);
      if (!response.ok) return [];
      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      if (!products.length) return [];

      const handles = products
        .filter((product) => String(product.id) !== String(productId) && product.handle)
        .map((product) => product.handle);

      if (this.maxItems > 0) return handles.slice(0, this.maxItems);
      return handles;
    } catch (error) {
      console.warn('Cross-sell fallback fetch failed:', error);
      return [];
    }
  }

  async fetchActiveProductHandles(productId) {
    const handles = [];
    const seenHandles = new Set();
    const pageSize = 250;
    let page = 1;

    try {
      while (true) {
        const response = await fetch(`/products.json?limit=${pageSize}&page=${page}`);
        if (!response.ok) break;

        const data = await response.json();
        const pageProducts = Array.isArray(data?.products) ? data.products : [];
        if (!pageProducts.length) break;

        pageProducts.forEach((product) => {
          const currentProductId = String(product?.id || '');
          const handle = String(product?.handle || '').trim();

          if (!currentProductId || currentProductId === String(productId) || !handle || seenHandles.has(handle)) return;

          seenHandles.add(handle);
          handles.push(handle);
        });

        if (this.maxItems > 0 && handles.length >= this.maxItems) {
          return handles.slice(0, this.maxItems);
        }

        if (pageProducts.length < pageSize) break;
        page += 1;
      }
    } catch (error) {
      console.warn('Cross-sell active products fetch failed:', error);
    }

    return handles;
  }

  async openForHandles(handles) {
    if (!this.list || !handles.length) return;

    const limitedHandles = this.maxItems > 0 ? handles.slice(0, this.maxItems) : handles;
    if (!limitedHandles.length) return;

    this.renderRequestId += 1;
    const currentRequestId = this.renderRequestId;
    this.list.innerHTML = '';

    const batchSize = 12;
    let hasShownDialog = false;

    for (let index = 0; index < limitedHandles.length; index += batchSize) {
      if (currentRequestId !== this.renderRequestId) return;

      const batchHandles = limitedHandles.slice(index, index + batchSize);
      const batchProducts = await Promise.all(batchHandles.map((handle) => this.fetchProduct(handle)));
      const availableProducts = batchProducts.filter((product) => this.getAvailableVariant(product));
      if (!availableProducts.length) continue;

      this.list.insertAdjacentHTML('beforeend', availableProducts.map((product) => this.renderProductCard(product)).join(''));

      if (!hasShownDialog && typeof this.dialogComponent.showDialog === 'function') {
        this.dialogComponent.showDialog();
        hasShownDialog = true;
      }
    }

    if (!hasShownDialog) {
      this.showEmptyState();
    }
  }

  showLoadingState() {
    if (!this.list) return;
    this.list.innerHTML = '<div class="cross-sell-popup__status">Cargando recomendaciones...</div>';

    if (typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    }
  }

  showEmptyState() {
    if (!this.list) return;
    this.list.innerHTML = '<div class="cross-sell-popup__status">No hay productos disponibles.</div>';
  }

  async fetchProduct(handle) {
    const cleanHandle = String(handle || '').trim();
    if (!cleanHandle) return null;
    if (this.productCache.has(cleanHandle)) return this.productCache.get(cleanHandle);

    try {
      const response = await fetch(`/products/${cleanHandle}.js`);
      if (!response.ok) return null;
      const product = await response.json();
      this.productCache.set(cleanHandle, product);
      return product;
    } catch (error) {
      console.warn('Cross-sell fetch failed:', error);
      return null;
    }
  }

  getAvailableVariant(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => variant.available) || product.variants[0] || null;
  }

  getProductImage(product) {
    if (!product) return '';
    if (product.featured_image) return product.featured_image;
    if (Array.isArray(product.images) && product.images.length > 0) return product.images[0];
    return '';
  }

  appendImageWidth(url, width) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

  formatMoney(cents) {
    const amount = Number(cents) / 100;
    if (Number.isNaN(amount)) return '';

    if (this.currencyCode) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
        }).format(amount);
      } catch (_) {
        // Fallback to symbol formatting below.
      }
    }

    if (this.currencySymbol) {
      return `${this.currencySymbol}${amount.toFixed(2)}`;
    }

    return amount.toFixed(2);
  }

  renderPrice(product) {
    const price = this.formatMoney(product.price);
    const compareAt = product.compare_at_price;

    if (compareAt && compareAt > product.price) {
      const compareAtFormatted = this.formatMoney(compareAt);
      return `
        <span class="cross-sell-popup__price-compare">${compareAtFormatted}</span>
        <span class="cross-sell-popup__price-sale">${price}</span>
      `;
    }

    return `<span class="cross-sell-popup__price-regular">${price}</span>`;
  }

  renderProductCard(product) {
    const variant = this.getAvailableVariant(product);
    const productUrl = `/products/${product.handle}`;
    const image = this.getProductImage(product);
    const title = this.escapeHtml(product.title || '');
    const isAvailable = Boolean(variant && variant.available);
    const buttonLabel = isAvailable ? this.addLabel : this.soldOutLabel;
    const buttonAttrs = isAvailable ? `data-variant-id="${variant.id}" data-cross-sell-add` : '';

    return `
      <div class="cross-sell-popup__item">
        <a class="cross-sell-popup__image" href="${productUrl}">
          ${
            image
              ? `<img src="${this.appendImageWidth(image, 300)}" alt="${title}" loading="lazy">`
              : '<span class="cross-sell-popup__image-placeholder"></span>'
          }
        </a>
        <div class="cross-sell-popup__meta">
          <div class="cross-sell-popup__meta-row cross-sell-popup__meta-row--labels">
            <span class="cross-sell-popup__meta-label">Nombre</span>
            <span class="cross-sell-popup__meta-label">Precio</span>
            <span class="cross-sell-popup__meta-label">Cantidad</span>
          </div>
          <div class="cross-sell-popup__meta-row cross-sell-popup__meta-row--values">
            <a class="cross-sell-popup__name" href="${productUrl}">${title}</a>
            <div class="cross-sell-popup__price">
              ${this.renderPrice(product)}
            </div>
            <div class="cross-sell-popup__quantity">
              <label class="visually-hidden" for="cross-sell-qty-${product.id}">Quantity</label>
              <button
                type="button"
                class="cross-sell-popup__qty-button"
                aria-label="Decrease quantity"
                data-cross-sell-qty-decrease
              >-</button>
              <input
                id="cross-sell-qty-${product.id}"
                class="cross-sell-popup__quantity-input"
                type="number"
                min="1"
                value="1"
                inputmode="numeric"
                aria-label="Quantity"
                data-cross-sell-qty
              >
              <button
                type="button"
                class="cross-sell-popup__qty-button"
                aria-label="Increase quantity"
                data-cross-sell-qty-increase
              >+</button>
            </div>
            <button class="button cross-sell-popup__add cross-sell-popup__add--mobile" ${buttonAttrs} ${isAvailable ? '' : 'disabled'}>
              ${buttonLabel}
            </button>
          </div>
        </div>
        <button class="button cross-sell-popup__add cross-sell-popup__add--desktop" ${buttonAttrs} ${isAvailable ? '' : 'disabled'}>
          ${buttonLabel}
        </button>
      </div>
    `;
  }

  onDialogClick = (event) => {
    const qtyButton = event.target.closest('[data-cross-sell-qty-decrease], [data-cross-sell-qty-increase]');
    if (qtyButton) {
      event.preventDefault();
      this.adjustQuantity(qtyButton);
      return;
    }

    const button = event.target.closest('[data-cross-sell-add]');
    if (!button) return;

    event.preventDefault();
    this.addVariantToCart(button);
  };

  adjustQuantity(button) {
    const wrapper = button.closest('.cross-sell-popup__quantity');
    const input = wrapper?.querySelector('[data-cross-sell-qty]');
    if (!input) return;

    const current = Number.parseInt(input.value, 10) || 1;
    const min = Number.parseInt(input.getAttribute('min') || '1', 10) || 1;
    const maxAttr = input.getAttribute('max');
    const max = maxAttr ? Number.parseInt(maxAttr, 10) : Number.NaN;
    const delta = button.hasAttribute('data-cross-sell-qty-increase') ? 1 : -1;

    let next = current + delta;
    if (!Number.isNaN(max)) next = Math.min(max, next);
    next = Math.max(min, next);

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async addVariantToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    const originalLabel = button.textContent;
    const item = button.closest('.cross-sell-popup__item');
    const qtyInput = item?.querySelector('[data-cross-sell-qty]');
    const quantity = Math.max(1, Number.parseInt(qtyInput?.value ?? '1', 10) || 1);

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = this.loadingLabel;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', String(quantity));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();
      await this.dispatchCartUpdate();

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.textContent = this.addedLabel;

      setTimeout(() => {
        button.classList.remove('is-added');
        button.disabled = false;
        button.textContent = originalLabel;
      }, 2000);
    } catch (error) {
      console.warn('Cross-sell add failed:', error);
      button.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }

  async dispatchCartUpdate() {
    this.suppressNext = true;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cross-sell-popup', {
          source: 'cross-sell-popup',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.warn('Cross-sell cart refresh failed:', error);
    }
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return char;
      }
    });
  }
}

new CrossSellPopup();
